// frontend/src/utils/logger.ts
// -----------------------------------------------------------------------------
// Production-Safe Logger Utility
// -----------------------------------------------------------------------------
// Prevents accidental exposure of internal state, PHI, and debug information
// in production clinical environments. Only outputs in development mode.
//
// Usage:
//   import logger from "utils/logger";
//   logger.debug("[Module] Debug info:", data);
//   logger.info("[Module] Informational message");
//   logger.warn("[Module] Warning:", issue);
//   logger.error("[Module] Error:", error);
// -----------------------------------------------------------------------------

const isDev = process.env.NODE_ENV === "development";

/* eslint-disable @typescript-eslint/no-unused-vars */
const noop = (..._args: unknown[]): void => {
  /* intentionally empty */
};
/* eslint-enable @typescript-eslint/no-unused-vars */

/**
 * Production-safe logger.
 * - In development: delegates to the native console methods.
 * - In production: all methods are silent no-ops.
 *
 * Bound methods can safely be passed as callbacks, e.g. `.catch(logger.error)`.
 */
const logger = {
  /** Verbose debug output (development only) */
  debug: isDev ? console.debug.bind(console) : noop,
  /** General informational messages (development only) */
  info: isDev ? console.info.bind(console) : noop,
  /** Warnings that don't block execution (development only) */
  warn: isDev ? console.warn.bind(console) : noop,
  /** Error reporting (development only) */
  error: isDev ? console.error.bind(console) : noop,
} as const;

export default logger;
